/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component (checkout customer-detail form: personal data, address,
 *              CF/PIVA derivation, field visibility/required rules)
 *   extends:   Component (@glimmer/component)
 *   template:  checkout-detail.hbs
 *   args:      @item, @confirm
 *   uses:      services store, notify, session; beebee-base/lib/list_taxes
 *              (paesiMondoAlfa3, paesiMondoAlfa2); ember-concurrency restartableTask
 *   siblings:  checkout-product
 *   tests:     checkout-detail-behaviour-test.js, checkout-detail-test.js
 *   gotchas:
 *     - showCodFiscale guards `this.args.item`: it is undefined during the async
 *       load window on /checkin (loadDataTask yields on GET /contacts/me while
 *       this component is already rendered); an unguarded `.get` there took
 *       down the whole app.
 *
 * Drives the checkout personal-data step: enables/disables name fields based
 * on existing contact data, derives placeholder CF/PIVA for foreign addresses,
 * and gates required fields via tenant option_field settings.
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { set } from '@ember/object';

import { typeOf } from '@ember/utils';
import { restartableTask, timeout } from 'ember-concurrency';

import { paesiMondoAlfa3, paesiMondoAlfa2 } from 'beebee-base/lib/list_taxes';

import { service } from '@ember/service';

export default class CheckoutDetailComponent extends Component {
  @service store;
  @service notify;
  @service session;

  @tracked showMore = null;
  @tracked firstNameDisabled = false;
  @tracked lastNameDisabled = false;
  @tracked ragioneSocialeDisabled = false;
  @tracked fisicaGiuridicaDisabled = false;

  @action
  setup(/*element*/) {
    this.disableField();
    this.checkAddressTask.perform();
  }

  get ecommerce_checkout_dati() {
    return this.session.option_field?.ecommerce_checkout_dati;
  }

  disableField() {
    this.firstNameDisabled = !!(
      this.args.item?.get('first_name') && this.args.item.get('first_name') !== ''
    );
    this.lastNameDisabled = !!(
      this.args.item?.get('last_name') && this.args.item.get('last_name') !== ''
    );
    this.ragioneSocialeDisabled = !!(
      this.args.item?.get('fisica_giuridica') &&
      this.args.item?.get('ragione_sociale') &&
      this.args.item.get('ragione_sociale') !== ''
    );

    let fisicaGiuridicaDisabled = false;
    if (this.args.item?.get('fisica_giuridica')) {
      fisicaGiuridicaDisabled = !!(
        this.args.item?.get('ragione_sociale') && this.args.item.get('ragione_sociale') !== ''
      );
    } else {
      fisicaGiuridicaDisabled = !!(
        this.args.item?.get('first_name') &&
        this.args.item.get('first_name') !== '' &&
        this.args.item?.get('last_name') &&
        this.args.item.get('last_name') !== ''
      );
    }
    this.fisicaGiuridicaDisabled = fisicaGiuridicaDisabled;
  }

  get hide_field() {
    if (!this.session.option_field) {
      return [];
    }
    return this.session.option_field?.ecommerce_hide_field;
  }

  get not_required_field() {
    if (!this.session.option_field) {
      return [];
    }
    return this.session.option_field?.ecommerce_not_required_field;
  }

  get isDateRequired() {
    if (!this.not_required_field) {
      return true;
    }
    return !(this.not_required_field?.data_nascita ? this.not_required_field?.data_nascita : false);
  }

  get isBirthRequired() {
    if (!this.not_required_field) {
      return true;
    }
    return !(this.not_required_field?.luogo_nascita
      ? this.not_required_field?.luogo_nascita
      : false);
  }

  get isMobileRequired() {
    if (!this.not_required_field) {
      return true;
    }
    return !(this.not_required_field?.mobile ? this.not_required_field?.mobile : false);
  }

  get isAddressRequired() {
    if (!this.not_required_field) {
      return true;
    }
    return !(this.not_required_field?.address ? this.not_required_field?.address : false);
  }

  completaStraniero() {
    const item = this.args.item;
    const contactAddress = item.addresses[0];
    const countryCode = contactAddress?.location_address_country_code;
    const contactId = item.id || '';

    if (!countryCode) {
      this.notify.warning('Seleziona prima un indirizzo!');
      return;
    }

    const convertCountryCodeToAlpha2 = (code) => {
      const index = paesiMondoAlfa3.indexOf(code);
      if (index === -1) {
        return code.substring(0, 2);
      }
      return paesiMondoAlfa2[index];
    };

    const alpha2Code = convertCountryCodeToAlpha2(countryCode);

    set(contactAddress, 'location_address_postal_code', '00000');
    set(item, 'codice_destinatario', 'XXXXXXX');

    if (item.fisica_giuridica) {
      const rimanentiCF = 14 - contactId.length;
      const contactCF = `${alpha2Code}${'00000000000000'.substring(0, rimanentiCF)}${contactId}`;
      set(item, 'cod_fiscale', contactCF);
    } else {
      set(item, 'cod_fiscale', null);
    }

    const rimanentiPIVA = 11 - contactId.length;
    const pIVA = `${alpha2Code}${'999999999'.substring(0, rimanentiPIVA)}${contactId}`;
    set(item, 'piva', pIVA);
  }

  get showCodFiscale() {
    // `@item` (the customer contact) is undefined during the async load window
    // on /checkin: loadDataTask yields on GET /contacts/me while this component
    // is already rendered. An unguarded `.get` here took down the whole app.
    const firstAddress = this.args.item?.get('addresses')?.[0];
    if (!firstAddress) {
      return false;
    }
    const country = firstAddress.get('location_address_country');
    const countryCode = firstAddress.get('location_address_country_code');
    if (country === 'Italia' || ['IT', 'ITA'].includes(countryCode)) {
      return true;
    }
    return false;
  }

  get isCFRequired() {
    const addresses = this.args.item?.get('addresses');
    const country =
      addresses && addresses[0] ? addresses[0].get('location_address_country') : undefined;

    if (country !== 'Italia') {
      return false;
    }

    if (!this.not_required_field) {
      return true;
    }

    return !(this.not_required_field?.cod_fiscale ? this.not_required_field?.cod_fiscale : false);
  }

  @action
  onItemChange() {
    const isDestroying = this.args.item?.isDestroying;
    const isDeleted = this.args.item?.isDeleted;
    const isDeleting = this.args.item?.isDeleting;
    if (isDeleted || isDeleting || isDestroying) {
      return;
    }

    this.checkAddressTask.perform();
    if (this.args.item?.id) {
      this.args.item.reload().catch(() => {});
    }
  }

  @restartableTask
  *checkAddressTask() {
    yield timeout(150);

    if (!this.args.item) {
      return;
    }

    if (!this.args.item.get('addresses') || this.args.item.get('addresses.length') === 0) {
      const newAddress = this.store.createRecord('address', {
        is_invoice: true,
        location_address_country: 'Italia',
        location_address_country_code: 'IT',
      });
      this.args.item.get('addresses').push(newAddress);
    }
  }

  @action
  formSubmit(e) {
    e.preventDefault();
    if (typeOf(this.args.confirm) === 'function') {
      this.args.confirm();
    }
  }
}
